import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isLocationGoneError, isStaleStateError } from "./access-errors";
import {
  addOrganizationMember,
  assignMemberLocations,
  changeMemberRole,
  reactivateMember,
  renameOrganization,
  type AddOrganizationMemberInput,
  type AssignMemberLocationsInput,
  type ChangeMemberRoleInput,
  type ReactivateMemberInput,
  type RenameOrganizationInput,
} from "./access-client";

/**
 * Roots of the admin reads an access write makes stale: every user read
 * (detail, memberships, staff) and every organization read (detail, members,
 * list). Prefix invalidation, so each feature keeps its own key layout.
 */
const STALE_ROOTS = [
  ["admin", "users"],
  ["admin", "organizations"],
] as const;

function useRefreshAdminReads() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(STALE_ROOTS.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}

/**
 * Shared mutation callbacks: refresh after a write, and also after a
 * stale-state or location-gone rejection (the screen was showing outdated
 * data), so the operator never retries against data the server has moved past.
 * The success refresh is not awaited: the dialog closes at once and the reads
 * catch up behind it. The error refresh IS awaited, so by the time the
 * caller's `catch` runs the reads are fresh.
 */
function useRefreshCallbacks() {
  const refresh = useRefreshAdminReads();
  return {
    onSuccess: () => {
      void refresh();
    },
    onError: (err: unknown) =>
      isStaleStateError(err) || isLocationGoneError(err) ? refresh() : undefined,
  };
}

/** Add a user to an organization as ADMIN or STAFF (no invitation is sent). */
export function useAddOrganizationMember() {
  return useMutation({
    mutationFn: (input: AddOrganizationMemberInput) => addOrganizationMember(input),
    ...useRefreshCallbacks(),
  });
}

/** Change a membership's role in place (STAFF <-> ADMIN). */
export function useChangeMemberRole() {
  return useMutation({
    mutationFn: (input: ChangeMemberRoleInput) => changeMemberRole(input),
    ...useRefreshCallbacks(),
  });
}

/** Add and remove locations of an ACTIVE STAFF member, all or nothing. */
export function useAssignMemberLocations() {
  return useMutation({
    mutationFn: (input: AssignMemberLocationsInput) => assignMemberLocations(input),
    ...useRefreshCallbacks(),
  });
}

/** Reactivate a deactivated ADMIN or STAFF member (locations are not restored). */
export function useReactivateMember() {
  return useMutation({
    mutationFn: (input: ReactivateMemberInput) => reactivateMember(input),
    ...useRefreshCallbacks(),
  });
}

/** Rename an organization (backoffice ADMIN). */
export function useRenameOrganization() {
  const refresh = useRefreshAdminReads();
  return useMutation({
    mutationFn: (input: RenameOrganizationInput) => renameOrganization(input),
    onSuccess: refresh,
  });
}
